// src/components/marketplace/ListOnMarketplace.js
import React, { useState, useEffect } from 'react';
import { BrowserProvider, Contract, parseUnits, formatUnits } from 'ethers';
import './ListOnMarketplace.css';

const MARKETPLACE_ADDRESS = process.env.REACT_APP_MARKETPLACE_ADDRESS || '0xA57B8268118871a68E1b76Fe8796F18F4934A7E5';
const EVENT_TICKET_ADDRESS = process.env.REACT_APP_EVENT_TICKET_ADDRESS || '0xbB045A636e23373Fa727a25F853aB039801ED79b';
const PGT_ADDRESS = process.env.REACT_APP_PGT_ADDRESS || '0xE59402C2C7537AfF3A3F1dCb300A37C362Dba5fc';

console.log('[ListOnMarketplace] Config:', { MARKETPLACE_ADDRESS, EVENT_TICKET_ADDRESS, PGT_ADDRESS });

const MARKETPLACE_ABI = [
  'function createFixedPriceListing(address nftContract, uint256 tokenId, uint256 amount, address paymentToken, uint256 price) external returns (uint256)',
  'function createAuctionListing(address nftContract, uint256 tokenId, uint256 amount, address paymentToken, uint256 startingPrice, uint256 duration) external returns (uint256)',
  'function paused() view returns (bool)',
  'function getSupportedTokens() view returns (address[])',
  'function isTokenSupported(address token) view returns (bool)',
];

const ERC1155_ABI = [
  'function setApprovalForAll(address operator, bool approved) external',
  'function isApprovedForAll(address account, address operator) view returns (bool)',
];

export default function ListOnMarketplace({ ticket, account, onClose }) {
  const [listingType, setListingType] = useState('fixed'); // 'fixed' or 'auction'
  const [price, setPrice] = useState('');
  const [amount, setAmount] = useState('1');
  const [paymentToken, setPaymentToken] = useState('0x0000000000000000000000000000000000000000'); // ETH
  const [auctionDuration, setAuctionDuration] = useState('7'); // days
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [step, setStep] = useState('form'); // 'form', 'approving', 'listing', 'done'
  const [userBalance, setUserBalance] = useState('0');
  const [isPaused, setIsPaused] = useState(false);
  const [supportedTokens, setSupportedTokens] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      if (!account || !window.ethereum) return;

      try {
        const provider = new BrowserProvider(window.ethereum);

        // Fetch user balance
        const balance = await provider.getBalance(account);
        setUserBalance(formatUnits(balance, 18));

        // Fetch marketplace status
        const marketplace = new Contract(MARKETPLACE_ADDRESS, MARKETPLACE_ABI, provider);

        // Check if paused
        const paused = await marketplace.paused();
        setIsPaused(paused);

        // Fetch supported tokens
        const tokens = await marketplace.getSupportedTokens();
        setSupportedTokens(tokens);
      } catch (err) {
        console.error('Error fetching data:', err);
      }
    };

    fetchData();
  }, [account]);

  const handleList = async () => {
    setError(null);
    setLoading(true);

    try {
      if (!window.ethereum) {
        throw new Error('Please install MetaMask or another Ethereum wallet');
      }

      if (isPaused) {
        throw new Error('Marketplace is currently paused. Please try again later.');
      }

      if (!price || parseFloat(price) <= 0) {
        throw new Error('Please enter a valid price greater than 0');
      }

      if (!amount || parseInt(amount) < 1) {
        throw new Error('Please enter a valid amount (minimum 1)');
      }

      const maxAvailable = ticket.amount || 1;
      if (parseInt(amount) > parseInt(maxAvailable)) {
        throw new Error(`You only have ${maxAvailable} tickets available`);
      }

      const provider = new BrowserProvider(window.ethereum);
      const signer = await provider.getSigner();
      const signerAddress = await signer.getAddress();

      if (signerAddress.toLowerCase() !== account.toLowerCase()) {
        throw new Error('Please connect with the wallet that owns this NFT');
      }

      // Step 1: Check approval
      setStep('approving');
      const eventTicketContract = new Contract(EVENT_TICKET_ADDRESS, ERC1155_ABI, signer);
      const isApproved = await eventTicketContract.isApprovedForAll(signerAddress, MARKETPLACE_ADDRESS);

      console.log('[ListOnMarketplace] Approval check:', { isApproved, owner: signerAddress, operator: MARKETPLACE_ADDRESS });

      if (!isApproved) {
        setError('Approving marketplace access...');
        console.log('[ListOnMarketplace] Requesting approval...');
        const approveTx = await eventTicketContract.setApprovalForAll(MARKETPLACE_ADDRESS, true);
        console.log('[ListOnMarketplace] Approval TX:', approveTx.hash);
        await approveTx.wait();
        console.log('[ListOnMarketplace] Approval confirmed');

        // Wait a bit for blockchain state to update
        await new Promise(resolve => setTimeout(resolve, 2000));

        // Verify approval was successful
        const isApprovedNow = await eventTicketContract.isApprovedForAll(signerAddress, MARKETPLACE_ADDRESS);
        console.log('[ListOnMarketplace] Approval after confirmation:', isApprovedNow);

        if (!isApprovedNow) {
          throw new Error('Approval failed - please try again');
        }
      }

      // Step 2: Create listing
      setStep('listing');
      const marketplaceContract = new Contract(MARKETPLACE_ADDRESS, MARKETPLACE_ABI, signer);

      let tx;
      const tokenId = ticket.tokenId.toString();
      const amountNumber = parseInt(amount);

      console.log('[ListOnMarketplace] Creating listing with params:', {
        nftContract: EVENT_TICKET_ADDRESS,
        tokenId,
        amount: amountNumber,
        paymentToken,
        listingType
      });

      if (listingType === 'fixed') {
        // Convert price to appropriate units using ethers parseUnits
        const priceInUnits = paymentToken === '0x0000000000000000000000000000000000000000'
          ? parseUnits(price, 18) // ETH (18 decimals)
          : parseUnits(price, 2); // PGT (2 decimals)

        console.log('[ListOnMarketplace] Fixed price listing:', {
          nftContract: EVENT_TICKET_ADDRESS,
          tokenId,
          amount: amountNumber,
          paymentToken,
          price: priceInUnits.toString()
        });

        tx = await marketplaceContract.createFixedPriceListing(
          EVENT_TICKET_ADDRESS,
          tokenId,
          amountNumber,
          paymentToken,
          priceInUnits
        );
      } else {
        // Auction
        const startingPriceInUnits = paymentToken === '0x0000000000000000000000000000000000000000'
          ? parseUnits(price, 18) // ETH (18 decimals)
          : parseUnits(price, 2); // PGT (2 decimals)

        const durationInSeconds = parseInt(auctionDuration) * 24 * 60 * 60; // days to seconds

        tx = await marketplaceContract.createAuctionListing(
          EVENT_TICKET_ADDRESS,
          tokenId,
          amountNumber,
          paymentToken,
          startingPriceInUnits,
          durationInSeconds
        );
      }

      setError('Transaction submitted. Waiting for confirmation...');
      await tx.wait();

      setStep('done');
      setSuccess(true);
      setLoading(false);

      // Close modal after 3 seconds
      setTimeout(() => {
        if (onClose) onClose();
      }, 3000);
    } catch (err) {
      console.error('[ListOnMarketplace] Error:', err);
      let errorMessage = err.reason || err.message || 'Failed to create listing';
      
      // Handle common user rejection errors
      if (errorMessage.toLowerCase().includes('user rejected')) {
        errorMessage = 'Transaction rejected by user';
      } else if (errorMessage.toLowerCase().includes('insufficient funds')) {
        errorMessage = 'Insufficient ETH balance for gas fees';
      } else if (errorMessage.toLowerCase().includes('gas')) {
        errorMessage = 'Transaction failed due to gas issues. Please try again.';
      }

      setError(errorMessage);
      setLoading(false);
      setStep('form');
    }
  };

  if (success && step === 'done') {
    return (
      <div className="list-marketplace-modal">
        <div className="list-marketplace-content">
          <div className="success-message">
            <div className="success-icon">✓</div>
            <h3>Listed Successfully!</h3>
            <p>Your ticket is now on the marketplace</p>
            <a
              href={`https://marketplace.example.com/ticket/${ticket.tokenId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="view-marketplace-btn"
            >
              View on Marketplace →
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="list-marketplace-modal" onClick={onClose}>
      <div className="list-marketplace-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>List Ticket on Marketplace</h3>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>

        {isPaused && (
          <div className="error-message">
            🚨 Marketplace is currently paused. Listing creation is temporarily disabled.
          </div>
        )}

        {!isPaused && parseFloat(userBalance) < 0.001 && (
          <div className="warning-message">
            ⚠️ Low ETH balance: {parseFloat(userBalance).toFixed(5)} ETH. You need ETH for gas fees.
          </div>
        )}

        {step === 'approving' && (
          <div className="loading-step">
            <div className="loader"></div>
            <p>Approving marketplace to transfer your NFTs...</p>
            <p className="step-hint">Please confirm the transaction in your wallet</p>
          </div>
        )}

        {step === 'listing' && (
          <div className="loading-step">
            <div className="loader"></div>
            <p>Creating listing...</p>
            <p className="step-hint">Please confirm the transaction in your wallet</p>
          </div>
        )}

        {step === 'form' && (
          <>
            <div className="ticket-preview">
              <p><strong>Token ID:</strong> {ticket.tokenId}</p>
              <p><strong>Ticket Type:</strong> {ticket.metadata?.name || ticket.ticketTypeId || 'Unknown'}</p>
              <p><strong>Available:</strong> {ticket.amount || 1}</p>
              <p><strong>Your ETH Balance:</strong> {parseFloat(userBalance).toFixed(5)} ETH</p>
            </div>

            <div className="form-group">
              <label>Listing Type</label>
              <div className="listing-type-selector">
                <button
                  className={listingType === 'fixed' ? 'type-btn active' : 'type-btn'}
                  onClick={() => setListingType('fixed')}
                  disabled={loading}
                >
                  Fixed Price
                </button>
                <button
                  className={listingType === 'auction' ? 'type-btn active' : 'type-btn'}
                  onClick={() => setListingType('auction')}
                  disabled={loading}
                >
                  Auction
                </button>
              </div>
            </div>

            <div className="form-group">
              <label>Payment Token</label>
              <select
                value={paymentToken}
                onChange={(e) => setPaymentToken(e.target.value)}
                disabled={loading || isPaused}
              >
                <option value="0x0000000000000000000000000000000000000000">ETH</option>
                {supportedTokens.length > 0 ? (
                  supportedTokens
                    .filter(token => token !== '0x0000000000000000000000000000000000000000')
                    .map(token => (
                      <option key={token} value={token}>
                        {token === PGT_ADDRESS ? 'PGT' : token.slice(0, 6) + '...' + token.slice(-4)}
                      </option>
                    ))
                ) : (
                  <option value={PGT_ADDRESS}>PGT</option>
                )}
              </select>
              <p className="input-hint">
                {supportedTokens.length} payment token{supportedTokens.length !== 1 ? 's' : ''} available
              </p>
            </div>

            <div className="form-group">
              <label>{listingType === 'fixed' ? 'Price' : 'Starting Price'}</label>
              <div className="price-input-container">
                <input
                  type="number"
                  step={paymentToken === PGT_ADDRESS ? "0.01" : "0.001"}
                  min="0.01"
                  value={price}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '' || /^(\d+\.?\d*|\.\d+)$/.test(value)) {
                      setPrice(value);
                    }
                  }}
                  placeholder={`0.${paymentToken === PGT_ADDRESS ? '00' : '000'}`}
                  disabled={loading}
                />
                <span className="currency-label">
                  {paymentToken === '0x0000000000000000000000000000000000000000' ? 'ETH' : 'PGT'}
                </span>
              </div>
              <p className="input-hint">
                You'll receive 95% after 5% marketplace fee. Min: {paymentToken === '0x0000000000000000000000000000000000000000' ? '0.001 ETH' : '1 PGT'}
              </p>
            </div>

            <div className="form-group">
              <label>Amount to List</label>
              <input
                type="number"
                min="1"
                max={ticket.amount || 1}
                value={amount}
                onChange={(e) => {
                  const value = e.target.value;
                  const maxValue = ticket.amount || 1;
                  if (value === '' || (/^\d+$/.test(value) && parseInt(value) <= parseInt(maxValue))) {
                    setAmount(value);
                  }
                }}
                disabled={loading}
              />
              <p className="input-hint">
                You have {ticket.amount || 1} tickets available
              </p>
            </div>

            {listingType === 'auction' && (
              <div className="form-group">
                <label>Auction Duration (days)</label>
                <input
                  type="number"
                  min="1"
                  max="30"
                  value={auctionDuration}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '' || (/^\d+$/.test(value) && parseInt(value) >= 1 && parseInt(value) <= 30)) {
                      setAuctionDuration(value);
                    }
                  }}
                  disabled={loading}
                />
                <p className="input-hint">
                  Auction will run for {auctionDuration} days ({parseInt(auctionDuration) * 24} hours)
                </p>
              </div>
            )}

            {error && (
              <div className="error-message">
                {error}
              </div>
            )}

            <div className="modal-actions">
              <button onClick={onClose} className="cancel-btn" disabled={loading}>
                Cancel
              </button>
              <button
                onClick={handleList}
                className="list-btn"
                disabled={
                  loading ||
                  isPaused ||
                  !price ||
                  !amount ||
                  parseFloat(price) <= 0 ||
                  parseInt(amount) < 1 ||
                  (ticket.amount && parseInt(amount) > parseInt(ticket.amount)) ||
                  parseFloat(userBalance) < 0.0005
                }
              >
                {loading ? (
                  <>
                    <span className="spinner"></span>
                    {step === 'approving' ? 'Approving...' : 'Creating...'}
                  </>
                ) : 'Create Listing'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}